import { domExtractTextContent } from '@findigs/helpers/src/helpers/dom-extract-text-content';
import { useIsomorphicLayoutEffect } from '@findigs/helpers/src/hooks/use-isomorphic-layout-effect';
import type { Location } from '@remix-run/react';
import { useLocation } from '@remix-run/react';
import { captureException } from '@sentry/remix';
import { useEffect, useId, useMemo } from 'react';
import type { BasePageAnalytics, BaseUserEventAnalytics } from '~/types';
import { rudderAnalyticsBrowser } from './rudderstack.client';

/** import type { ApiObject } from '@rudderstack/analytics-js'; */
type ApiObject = {
  [index: string]:
    | string
    | number
    | boolean
    | ApiObject
    | null
    | Date
    | (string | number | boolean | null | Date | ApiObject | undefined)[]
    | undefined;
};

const CUSTOM_ATTR = 'data-rs';

export type UseRudderStackProps = {
  userId?: string;
};

export type OnPageChangedProps = UseRudderStackProps & {
  location: Location;
  href: string;
};

type CustomPageDataReservedFields =
  | 'name'
  | 'path'
  | 'url'
  | 'title'
  | 'referrer'
  | 'search'
  | 'keywords';

export type CustomPageData = ApiObject & {
  [key in CustomPageDataReservedFields]?: never;
};

type CustomComponentDataReservedFields = 'html';

export type CustomComponentData = ApiObject & {
  [key in CustomComponentDataReservedFields]?: never;
};

let referrer = typeof document !== 'undefined' ? document.referrer : '';
let viewed: { href: string; userId?: string } = { href: '', userId: undefined };
let prevUserId: string | undefined = undefined;

/**
 * contains custom page data to be sent in the page event
 */
const customPageData: CustomPageData[] = [];

/**
 * contains the custom page data of the current page,
 * to be sent in non-page events
 */
let customPageDataCurrentPage: CustomPageData | undefined = undefined;

/**
 * contains custom component data to be sent in the click events
 */
const customCompontData = new Map<string, CustomComponentData>();

function onPageChanged({ userId, location, href }: OnPageChangedProps) {
  if (prevUserId !== userId) {
    if (userId !== undefined) {
      // user logged in
      rudderAnalyticsBrowser.identify(userId);
    } else if (prevUserId !== undefined) {
      // user logged out
      rudderAnalyticsBrowser.reset();
    }

    prevUserId = userId;
  }

  const customData = customPageData.pop();
  customPageDataCurrentPage = customData;

  rudderAnalyticsBrowser.page({
    ...customData,
    path: location.pathname,
    search: location.search,
    url: href,
    referrer,
  } satisfies BasePageAnalytics);
}

function usePageTracker(props: UseRudderStackProps) {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname + location.search;
    const href = window.location.origin + path;
    const { userId } = props;

    if (viewed.href !== href || viewed.userId !== userId) {
      viewed = { href, userId };

      onPageChanged({ ...props, location, href });
    }

    if (referrer !== href) {
      try {
        /**
         * Tries also overriding `document.referrer` so that rudderstack sends the
         * correct referrer value in non-page events, despite client-side routing
         *
         * @remarks this is intentionally using the "stale" value from our `referrer` variable`,
         * as that's actually the "current" value. The updated value is the "next" value,
         * which is not what we want.
         *
         * In other words, this has been intentionally placed before `referrer = href`
         */
        Object.defineProperty(document, 'referrer', {
          configurable: true,
          value: referrer,
        });
      } catch (ex) {
        captureException(ex);
      }

      referrer = href;
    }
  }, [location, props]);
}

function captureContext(initialTarget: HTMLElement) {
  const target = initialTarget.closest(
    'button,a[href],input,select,textarea'
  ) as HTMLElement | null;

  if (!target) return;

  /**
   * traversing DOM tree collecting data from all ancestors that have
   * CUSTOM_ATTR set
   */
  const data: CustomComponentData = {};
  for (let t: HTMLElement | null = target; t != null; ) {
    const found: HTMLElement | null = t.closest(`[${CUSTOM_ATTR}]`);

    const keys = found?.getAttribute(CUSTOM_ATTR);
    if (keys != null) {
      Object.assign(
        data,
        ...keys.split(' ').map((key) => customCompontData.get(key))
      );
    }

    t = found?.parentElement ?? null;
  }

  const text = domExtractTextContent(target) ?? undefined;
  const tag = target.tagName.toLowerCase();
  const tag_type = target.getAttribute('type')?.toLowerCase() ?? undefined;
  const href = target.getAttribute('href') ?? undefined;
  const name = target.getAttribute('name') ?? undefined;
  const value =
    'value' in target &&
    target.value != null &&
    (tag !== 'button' || target.value !== '')
      ? (target.value as string)
      : undefined;
  const checked =
    'checked' in target &&
    tag === 'input' &&
    (tag_type === 'checkbox' || tag_type === 'radio')
      ? (target.checked as boolean)
      : undefined;

  return {
    ...data,
    html: { text, tag, tag_type, name, href, value, checked },
  } satisfies BaseUserEventAnalytics;
}

function useClickTracker() {
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const ctx = captureContext(e.target as HTMLElement);
      if (!ctx) return;

      rudderAnalyticsBrowser.track('click', {
        ...customPageDataCurrentPage,
        ...ctx,
      });
    };

    /**
     * capture ensures that this ev listener runs before everything else
     * so we can capture before any state updates happen
     */
    document.addEventListener('click', onClick, {
      passive: true,
      capture: true,
    });
    return () => {
      document.removeEventListener('click', onClick);
    };
  }, []);
}

/**
 * Hook that internally implements the RudderStack analytics integration
 *
 * Should only ever be called in `root.tsx`
 */
export function useRudderStackIntegrationRoot(props: UseRudderStackProps) {
  usePageTracker(props);
  useClickTracker();
}

/**
 * Hook to be used inside pages to augment page events with page-relevant
 * custom data
 *
 * Should only ever be called once per page, and only if the page
 * requires custom data to be collected and sent.
 *
 * Basic page data is handled automatically, even if not calling
 * this hook. Thus, **calling this is optional**.
 */
export function useAnalyticsAugmentPage<E extends CustomPageData>(data: E) {
  useIsomorphicLayoutEffect(() => {
    // clears any stale data
    customPageData.splice(0);

    customPageData.push(data);

    // precise clean up to avoid race conditions
    return () => {
      const i = customPageData.lastIndexOf(data);
      if (i !== -1) customPageData.splice(i, 1);
    };
  }, [data]);
}

/**
 * Hook to be used in components to augment the tracking of events with component-relevant
 * contextual custom data
 *
 * Returns props that should be spread and passed to the components' outermost elements.
 *
 * Basic component event data is handled automatically, even if not calling
 * this hook. Thus, **calling this is optional**.
 *
 * Similar to `useAnalyticsAugmentComponent` but useful when working with an array of components.
 *
 * Any events originating from elements inside these container components will also contain the information
 * provided to `useAnalyticsAugmentComponents`.
 */
export function useAnalyticsAugmentComponents<E extends CustomComponentData>(
  props: { data: E; mergeWith?: {} | { [CUSTOM_ATTR]?: string } }[]
) {
  const idPrefix = useId();
  const ids = useMemo(
    () => props.map((_, i) => `${idPrefix}__${i}`),
    [idPrefix, props]
  );

  useEffect(() => {
    for (let i = 0; i < props.length; i++) {
      const id = ids[i];
      const { data } = props[i];

      customCompontData.set(id, data);
    }

    return () => {
      for (const id of ids) {
        customCompontData.delete(id);
      }
    };
  }, [props, ids]);

  return useMemo(
    () =>
      props.map(({ mergeWith }, i) => ({
        [CUSTOM_ATTR]: [
          ids[i],
          mergeWith ? (mergeWith as any)[CUSTOM_ATTR] : undefined,
        ]
          .filter((id) => id != null)
          .join(' '),
      })),
    [ids, props]
  );
}

/**
 * Hook to be used in components to augment the tracking of events with component-relevant
 * contextual custom data
 *
 * Returns props that should be spread and passed to the component's outermost element.
 *
 * Basic component event data is handled automatically, even if not calling
 * this hook. Thus, **calling this is optional**.
 *
 * @example```tsx
 *  () => (
 *    <section {...useAnalyticsAugmentComponent({ title: 'A', whatever: 'B' })}>
 *      <p>123</p>
 *      <button>click me</button>
 *    </section>
 *  )
 *```
 *
 * Any events originating from elements inside the container component will also contain the information
 * provided to `useAnalyticsAugmentComponent`.
 */
export function useAnalyticsAugmentComponent<E extends CustomComponentData>(
  data: E,
  mergeWith?: {} | { [CUSTOM_ATTR]?: string }
) {
  return useAnalyticsAugmentComponents([{ data, mergeWith }])[0];
}
